import {useCallback, useState} from "react";
import {useTranslation} from "react-i18next";
import type {ChatMessageDTO, ConversationDTO} from "../types";
import {fetchConversation, markChatRead, postMessage as apiPostMessage} from "../api/conversationApi";
import {useSnackbarEx} from "@/lib/snackbarex";
import {NotificationI18N} from "@/translations";

/**
 * Result shape returned by `useConversation`.
 */
export interface UseConversationResult {
    conversation: ConversationDTO | undefined;
    isLoading: boolean;
    loadError: unknown;
    /**
     * True once the first load attempt has completed (successfully or with an
     * error). Stays `false` until after the initial `load()` call resolves or
     * rejects. Consumers use this to avoid rendering a panel before the first
     * network response has arrived.
     */
    hasLoaded: boolean;
    /**
     * Loads (or reloads) the conversation for the given requestId.
     * Called on mount via `useEffect` and whenever the user triggers a
     * manual refresh button or after sending a message.
     */
    load: (requestId: number) => Promise<void>;
    /**
     * Posts a message to a chat and refreshes the conversation so the caller
     * sees the new entry without an extra `load()` call.
     *
     * On network/server error, shows an error snackbar and re-throws so the
     * caller (MessageComposer) can reset its sending state.
     */
    sendMessage: (requestId: number, chatId: number, body: string, attachments?: File[]) => Promise<ChatMessageDTO>;
    /**
     * Marks a chat as read and updates the local unread count optimistically.
     */
    markRead: (requestId: number, chatId: number) => Promise<void>;
}

/**
 * Hook for loading and managing the conversation state for a single request.
 *
 * This hook is the single source of truth for `conversation`. The conversation
 * is loaded once on mount (triggered by the consumer via `useEffect`) and can
 * be reloaded at any time by calling `load(requestId)` — e.g. from a manual
 * refresh button or after a message is sent. There is no background polling.
 *
 * The divider position is driven entirely by `ChatMessageDTO.read` from the
 * backend — no session-scoped anchor map is needed.
 */
export function useConversation(): UseConversationResult {
    const [conversation, setConversation] = useState<ConversationDTO | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState<unknown>(undefined);
    const [hasLoaded, setHasLoaded] = useState(false);
    const {t} = useTranslation();
    const {showMessage} = useSnackbarEx();

    const load = useCallback(async (requestId: number) => {
        setIsLoading(true);
        setLoadError(undefined);
        try {
            const data = await fetchConversation(requestId);
            setConversation(data);
            setHasLoaded(true);
        } catch (error: unknown) {
            setLoadError(error);
            setHasLoaded(true);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const sendMessage = useCallback(
        async (requestId: number, chatId: number, body: string, attachments?: File[]): Promise<ChatMessageDTO> => {
            try {
                const message = await apiPostMessage(requestId, chatId, body, attachments);
                // Reload the conversation so timeline reflects the new message.
                const updated = await fetchConversation(requestId);
                setConversation(updated);
                return message;
            } catch (error: unknown) {
                showMessage({
                    severity: "error",
                    summary: t(NotificationI18N.chat.composer.sendFailed),
                    error
                });
                throw error;
            }
        },
        [showMessage, t]
    );

    const markRead = useCallback(async (requestId: number, chatId: number) => {
        await markChatRead(requestId, chatId);
        // Optimistically reset the unread count for this chat in local state.
        setConversation((prev) => {
            if (!prev) {
                return prev;
            }
            return {
                ...prev,
                chats: prev.chats?.map((chat) => (chat.id === chatId ? {...chat, unreadCount: 0} : chat))
            };
        });
    }, []);

    return {
        conversation,
        isLoading,
        loadError,
        hasLoaded,
        load,
        sendMessage,
        markRead
    };
}
